import { type Letter, letters as lettersApi } from '@/lib/api';
import { useDateLocale } from '@/lib/hooks/use-date-locale';
import { routes } from '@/lib/routes';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { Loader2, Plane } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

export default function InTransitPage() {
  const { t } = useTranslation();
  const [letters, setLetters] = useState<Letter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLetters = async () => {
      try {
        const data = await lettersApi.getInTransit();
        setLetters(data ?? []);
      } catch (error) {
        console.error('Failed to fetch in-transit letters:', error);
        setLetters([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLetters();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-serif text-ink">{t('inTransit.title')}</h1>
        <p className="text-sepia/70">{t('inTransit.subtitle')}</p>
      </div>

      {letters.length === 0 ? (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-warm/30 mb-4">
            <Plane className="w-8 h-8 text-sepia/50" />
          </div>
          <h3 className="text-lg font-medium text-ink mb-2">{t('inTransit.empty')}</h3>
          <p className="text-sepia/70">{t('inTransit.emptyDesc')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {letters.map((letter, index) => (
            <InTransitCard key={letter.id} letter={letter} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

function InTransitCard({ letter, index }: { letter: Letter; index: number }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const deliveryTime = letter.deliveredAt ? new Date(letter.deliveredAt) : null;
  const sentTime = letter.sentAt ? new Date(letter.sentAt) : new Date();

  // Calculate progress
  const now = new Date();
  const totalTime = deliveryTime ? deliveryTime.getTime() - sentTime.getTime() : 0;
  const elapsed = now.getTime() - sentTime.getTime();
  const progress = totalTime > 0 ? Math.min((elapsed / totalTime) * 100, 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Link to={routes.letter(letter.id)}>
        <div
          className="bg-white rounded-lg p-6 cursor-pointer hover:translate-y-[-2px] transition-all"
          style={{ boxShadow: 'var(--shadow-md)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-sepia/60">
                {t('letter.to')}: {letter.recipient?.username || 'Unknown'}
              </p>
              <h3 className="text-lg font-medium text-ink">{letter.subject}</h3>
            </div>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
            >
              <Plane className="w-6 h-6 text-blue" />
            </motion.div>
          </div>

          {/* Journey visualization */}
          <div className="relative h-3 bg-gray-warm/30 rounded-full overflow-hidden mb-3">
            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue to-sage rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Timing info */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-sepia/60">
              {t('letter.sentOn', {
                date: formatDistanceToNow(sentTime, { addSuffix: true, locale: dateLocale })
              })}
            </span>
            {deliveryTime && (
              <span className="text-blue font-medium">
                {t('letter.arrivesIn', {
                  date: formatDistanceToNow(deliveryTime, { addSuffix: true, locale: dateLocale })
                })}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
